import React, { useEffect, Suspense, lazy, useMemo } from "react";
import { Switch, Route, Redirect, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import PrivateRoute from "src/utils/PrivateRoute";
import LogOut from "./utils/LogOut";
import { loadUser } from "src/store/Client";
import * as ROUTES from "./utils/constants/routes";
import Loading from "src/components/Loading";
import HeaderItems from "src/components/HeaderItems";
import useClient from "src/store/hooks/useClient";
import FinaLayout from "./components/FinaLayout";
import { useInvestments, useCustomInvestments } from "src/store/hooks";
import BrokerageOrders from "./pages/Brokerage/orders";
import { useAnalytics } from "./hooks/useAnalytics";
import GoalBasedPredictions from "./pages/GoalBased/predictions";
import NiceModal from "@ebay/nice-modal-react";

/* The public-market invest flow is a DIALOG now, opened from a smartfolio's
   page. The route stays reachable and opens that dialog over the smartfolio —
   see Modals/Invest/OpenOnRoute. */
const OpenInvestOnRoute = lazy(
  () => import("./components/Modals/Invest/OpenOnRoute"),
);
const OpenInvestPrivateOnRoute = lazy(
  () => import("./components/Modals/InvestPrivate/OpenOnRoute"),
);
const Dashboard = lazy(() => import("./pages/Dashboard"));
import OpenOnRoute from "./components/Modals/CashFlows/OpenOnRoute";
import {
  DEPOSIT_CASH_MODAL,
  WITHDRAW_CASH_MODAL,
} from "./components/Modals/CashFlows";
/* The import itself sits behind the environment check, not just the route.
   `process.env.NODE_ENV` is substituted at build time, so in a production
   build this whole expression folds to `null` and the `import()` disappears
   with it — no chunk is emitted. Putting `lazy()` at module scope and only
   guarding the <Route> left the gallery and its fixtures in the build,
   unreachable but shipped. */
const DialogGallery =
  process.env.NODE_ENV === "development"
    ? lazy(() => import("./pages/DialogGallery"))
    : null;
const Settings = lazy(() => import("./pages/Settings"));
const TempLogin = lazy(() => import("./pages/TempLogin"));
const Invest = lazy(() => import("./pages/Invest"));

const Brokerage = lazy(() => import("./pages/Brokerage"));
const BrokerageDetails = lazy(() => import("./pages/Brokerage/details"));

// const OurSmartfolios = lazy(() => import("./pages/OurSmartfolios"));
const InvestmentDetails = lazy(() => import("./pages/InvestmentDetails"));
const NotFound = lazy(() => import("./pages/Error/NotFound"));
const Error = lazy(() => import("./pages/Error"));
const FindSmartfolio = lazy(() => import("./pages/FindSmartfolio"));
const Markets = lazy(() => import("./pages/Markets"));
const GoalBased = lazy(() => import("./pages/GoalBased"));
const SingleSmartfolio = lazy(() => import("./pages/SingleSmartfolio/"));
const SmartfolioAnalytics = lazy(() => import("./pages/SmartfolioAnalytics"));

const App: React.FC = () => {
  useAnalytics();
  const dispatch = useDispatch();
  const location: any = useLocation();
  const selectedSmartfolio: string | undefined = useMemo(
    () => location.state?.name,
    [location.state],
  );

  const CurrentClient = useClient();
  const user = {
    ...CurrentClient,
    // Loaded once is enough. A later hourly refetch that fails for want of a
    // session ends it on its own (see utils/session); one that fails because
    // the gateway hiccuped should not take the page away.
    isAuthenticated: Boolean(CurrentClient.data),
  };
  // useInvestments();
  // useCustomInvestments();
  // NOTE: we're using this for the temp login
  useEffect(() => {
    dispatch(loadUser());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only an authenticated client gets this far: index.tsx renders the app
  // inside SessionGate, which holds it back until /me has answered and sends
  // anyone without a session to sign in.

  return (
    <FinaLayout>
      <Suspense fallback={<Loading />}>
        <Switch>
          {/* Every dialog in the app on one page — see pages/DialogGallery.
              Local development only: it mounts dialogs from fixtures, which
              is a tool for building them, not something to leave reachable in
              a deployed environment. */}
          {DialogGallery && (
            <Route exact path="/dialogs">
              <DialogGallery />
            </Route>
          )}

          <Redirect exact from="/" to={ROUTES.DASHBOARD} />

          <PrivateRoute user={user} path={ROUTES.DASHBOARD}>
            <Dashboard user={user} />
          </PrivateRoute>

          {/* <PrivateRoute exact user={user} path={ROUTES.OURSMARTFOLIOS}>
            <OurSmartfolios user={user} />
          </PrivateRoute> */}

          <PrivateRoute exact user={user} path={ROUTES.INVEST}>
            <Invest user={user} selectedSmartfolioName={selectedSmartfolio} />
          </PrivateRoute>

          <PrivateRoute exact user={user} path={ROUTES.MARKETS}>
            <Markets />
          </PrivateRoute>

          <PrivateRoute exact user={user} path={ROUTES.GOAL_BASED}>
            <GoalBased />
          </PrivateRoute>

          <PrivateRoute exact user={user} path={ROUTES.GOAL_PREDICTIONS}>
            <GoalBasedPredictions />
          </PrivateRoute>

          <PrivateRoute exact user={user} path={ROUTES.BROKERAGE}>
            <Brokerage user={user} />
          </PrivateRoute>

          <PrivateRoute exact user={user} path={ROUTES.BROKERAGE_DETAILS}>
            <BrokerageDetails user={user} />
          </PrivateRoute>

          <PrivateRoute
            exact
            user={user}
            path={`${ROUTES.BROKERAGE_ORDERS}/:ticker_id/:symbol`}
          >
            <BrokerageOrders user={user} />
          </PrivateRoute>

          {/* Both cash flows are dialogs now; the routes open them over the
              dashboard so existing links and bookmarks still work. */}
          <PrivateRoute user={user} path={ROUTES.DEPOSIT}>
            <OpenOnRoute modal={DEPOSIT_CASH_MODAL} />
          </PrivateRoute>

          <PrivateRoute exact user={user} path={ROUTES.SINGLESMARTFOLIO}>
            <SingleSmartfolio />
          </PrivateRoute>
          {/* One smartfolio, one page. Private equity and structured notes
              had a second one of their own, so which version of a smartfolio
              a reader saw depended on the link they followed, and the two
              disagreed about what it was. The route stays so existing links
              and bookmarks resolve; it sends them to the page that carries
              the terms, the holding and the documents. */}
          <Route
            exact
            path={ROUTES.PRIVATESMARTFOLIO}
            render={({ match }) => (
              <Redirect
                to={ROUTES.SINGLESMARTFOLIO.replace(
                  ":id",
                  String(match.params.id),
                )}
              />
            )}
          />
          <PrivateRoute exact user={user} path={ROUTES.GOAL_BASED_SMARTFOLIO}>
            <SmartfolioAnalytics />
          </PrivateRoute>

          <PrivateRoute user={user} path={ROUTES.WITHDRAW}>
            <OpenOnRoute modal={WITHDRAW_CASH_MODAL} />
          </PrivateRoute>

          <PrivateRoute user={user} path={ROUTES.SETTINGS}>
            <Settings user={user} />
          </PrivateRoute>

          <PrivateRoute user={user} path={`${ROUTES.INVESTMENT_DETAILS}/:id`}>
            <InvestmentDetails />
          </PrivateRoute>

          <PrivateRoute user={user} path={`${ROUTES.OURSMARTFOLIOS}`}>
            <FindSmartfolio />
          </PrivateRoute>

          <PrivateRoute user={user} path={`${ROUTES.NEW_INVEST_PUBLIC}`}>
            <OpenInvestOnRoute />
          </PrivateRoute>

          {/* Reserving an allocation is a dialog over the smartfolio now,
              the way a public-market order already is. Both routes open it
              over the page they belong to. */}
          <PrivateRoute user={user} path={`${ROUTES.NEW_INVEST_PRIVATE}`}>
            <OpenInvestPrivateOnRoute />
          </PrivateRoute>
          <PrivateRoute user={user} path={`${ROUTES.NEW_INVEST_STRUCTURED}`}>
            <OpenInvestPrivateOnRoute />
          </PrivateRoute>

          <Route path={ROUTES.LOGIN} component={TempLogin} />
          <Route path={ROUTES.LOGOUT} component={LogOut} />
          <Route path={ROUTES.ERROR} render={(props) => <Error {...props} />} />
          <Route component={NotFound} />
        </Switch>
      </Suspense>
    </FinaLayout>
  );
};

export default App;
